import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";

import {
  Search,
  CalendarDays,
  ArrowUpRight,
  Newspaper,
  Sparkles,
  Clock,
  X,
  RefreshCw,
  ChevronRight,
  TrendingUp,
  BookOpen,
} from "lucide-react";

import LogoFallback from "/img/logo.png";

export default function NewsList({
  logoSrc = LogoFallback,
  accent = "#014864",
  pageTitle = "News | University of Creative Technology Chittagong",
}) {
  const [news, setNews] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --------------------------------------------------
  // Fetch News
  // --------------------------------------------------

  const newsPost = async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        userId: import.meta.env.VITE_API_USER,
        password: import.meta.env.VITE_API_PASS,
      };

      const api = axios.create({
        baseURL: import.meta.env.VITE_API_BASE_URL,
        headers: {
          "Content-Type": "application/json",
        },
      });

      const response = await api.post("/all-news", payload);

      const data = Array.isArray(response.data)
        ? response.data
        : [];

      setNews(data);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Something went wrong"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    newsPost();
  }, []);

  // --------------------------------------------------
  // Browser Title
  // --------------------------------------------------

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  // --------------------------------------------------
  // Helpers
  // --------------------------------------------------

  const formatDate = (dateString) => {
    if (!dateString) return "Date unavailable";

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return dateString;
    }

    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getImageUrl = (path) => {
    if (!path) return "";

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const baseUrl = import.meta.env.VITE_API_SOURCE_URL || "";

    return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
  };

  // --------------------------------------------------
  // Search News
  // --------------------------------------------------

  const filteredNews = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    return news
      .filter((item) => {
        const title = String(item.head_line || "").toLowerCase();
        const date = String(item.date || "").toLowerCase();

        return (
          title.includes(keyword) ||
          date.includes(keyword) ||
          formatDate(item.date).toLowerCase().includes(keyword)
        );
      })
      .slice(0, 48);
  }, [news, search]);

  // --------------------------------------------------
  // Animations
  // --------------------------------------------------

  const containerVariants = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  // --------------------------------------------------
  // Render
  // --------------------------------------------------

  return (
    <div
      className="min-h-screen bg-slate-50 overflow-hidden"
      style={{
        "--accent": accent,
      }}
    >
      {/* ================================================
          HERO SECTION
      ================================================ */}

      <section className="relative bg-[#014864] text-white overflow-hidden">

        {/* Background Gradient */}

        <div className="absolute inset-0 bg-gradient-to-br from-[#014864] via-[#036987] to-[#0c9db0]" />

        {/* Animated Background Elements */}

        <motion.div
          animate={{
            x: [0, 40, 0],
            y: [0, -30, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-cyan-300/10 blur-3xl"
        />

        <motion.div
          animate={{
            x: [0, -30, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute -bottom-40 -left-40 w-[450px] h-[450px] rounded-full bg-teal-300/10 blur-3xl"
        />

        {/* Grid Background */}

        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-20 md:py-28">

          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* Hero Content */}

            <motion.div
              initial={{
                opacity: 0,
                x: -50,
              }}
              animate={{
                opacity: 1,
                x: 0,
              }}
              transition={{
                duration: 0.8,
              }}
            >

              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-md mb-6">

                <Sparkles
                  size={16}
                  className="text-yellow-300"
                />

                <span className="text-sm font-medium tracking-wide">
                  University News & Updates
                </span>

              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">

                UCTC

                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-200 to-teal-200">
                  News & Stories
                </span>

              </h1>

              <p className="mt-6 text-white/75 text-base md:text-lg leading-relaxed max-w-xl">

                Discover the latest achievements, events,
                campus activities, and inspiring stories from
                the University of Creative Technology Chittagong.

              </p>

              {/* Statistics */}

              <div className="flex flex-wrap gap-4 mt-8">

                <div className="flex items-center gap-3 bg-white/10 border border-white/10 rounded-2xl px-5 py-4 backdrop-blur-md">

                  <div className="w-11 h-11 rounded-xl bg-cyan-300/20 flex items-center justify-center">

                    <Newspaper
                      size={22}
                      className="text-cyan-200"
                    />

                  </div>

                  <div>

                    <p className="text-2xl font-bold">
                      {news.length}
                    </p>

                    <p className="text-xs text-white/60">
                      Published News
                    </p>

                  </div>

                </div>

                <div className="flex items-center gap-3 bg-white/10 border border-white/10 rounded-2xl px-5 py-4 backdrop-blur-md">

                  <div className="w-11 h-11 rounded-xl bg-teal-300/20 flex items-center justify-center">

                    <TrendingUp
                      size={22}
                      className="text-teal-200"
                    />

                  </div>

                  <div>

                    <p className="text-2xl font-bold">
                      {filteredNews.length}
                    </p>

                    <p className="text-xs text-white/60">
                      News Found
                    </p>

                  </div>

                </div>

              </div>

            </motion.div>

            {/* Hero Illustration */}

            <motion.div
              initial={{
                opacity: 0,
                scale: 0.8,
                rotate: -5,
              }}
              animate={{
                opacity: 1,
                scale: 1,
                rotate: 0,
              }}
              transition={{
                duration: 0.8,
                delay: 0.2,
              }}
              className="relative hidden md:flex justify-center items-center"
            >

              <motion.div
                animate={{
                  y: [0, -12, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="relative w-80 h-80 lg:w-[400px] lg:h-[400px]"
              >

                {/* Circles */}

                <div className="absolute inset-0 rounded-full border border-white/20" />

                <div className="absolute inset-8 rounded-full border border-white/10" />

                <div className="absolute inset-16 rounded-full bg-white/5 backdrop-blur-sm" />

                {/* Newspaper Illustration */}

                <div className="absolute inset-0 flex items-center justify-center">

                  <div className="w-48 h-60 bg-white rounded-3xl shadow-2xl p-5 rotate-[-5deg]">

                    <div className="w-12 h-12 rounded-2xl bg-[#014864] flex items-center justify-center mb-5">

                      <Newspaper
                        size={25}
                        className="text-white"
                      />

                    </div>

                    <div className="space-y-3">

                      <div className="h-3 w-28 rounded-full bg-[#014864]" />

                      <div className="h-2 w-full rounded-full bg-slate-200" />

                      <div className="h-2 w-4/5 rounded-full bg-slate-200" />

                      <div className="h-2 w-full rounded-full bg-slate-200" />

                      <div className="h-2 w-3/5 rounded-full bg-slate-200" />

                    </div>

                    <div className="mt-7 flex items-center gap-2">

                      <div className="w-8 h-8 rounded-lg bg-cyan-100" />

                      <div className="flex-1 space-y-2">

                        <div className="h-2 w-full rounded-full bg-slate-200" />

                        <div className="h-2 w-2/3 rounded-full bg-slate-200" />

                      </div>

                    </div>

                  </div>

                </div>

                {/* Floating Icons */}

                <motion.div
                  animate={{
                    y: [0, -10, 0],
                    rotate: [0, 5, 0],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                  }}
                  className="absolute top-12 right-0 w-14 h-14 rounded-2xl bg-white shadow-xl flex items-center justify-center"
                >

                  <BookOpen
                    size={25}
                    className="text-[#014864]"
                  />

                </motion.div>

                <motion.div
                  animate={{
                    y: [0, 10, 0],
                    rotate: [0, -5, 0],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                  }}
                  className="absolute bottom-12 left-0 w-14 h-14 rounded-2xl bg-cyan-300 shadow-xl flex items-center justify-center"
                >

                  <Sparkles
                    size={25}
                    className="text-[#014864]"
                  />

                </motion.div>

              </motion.div>

            </motion.div>

          </div>

        </div>

        {/* Bottom Wave */}

        <div className="relative -mb-1">

          <svg
            viewBox="0 0 1440 100"
            className="w-full h-16 md:h-24"
            preserveAspectRatio="none"
          >

            <path
              d="M0,50 C240,100 480,0 720,50 C960,100 1200,0 1440,50 L1440,100 L0,100 Z"
              fill="#f8fafc"
            />

          </svg>

        </div>

      </section>

      {/* ================================================
          NEWS SECTION
      ================================================ */}

      <section className="relative py-16 md:py-20 px-4 sm:px-8 lg:px-16">

        <div className="max-w-7xl mx-auto">

          {/* Section Heading */}

          <motion.div
            initial={{
              opacity: 0,
              y: 25,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.6,
            }}
            className="text-center mb-12"
          >

            <div className="inline-flex items-center gap-2 text-[#014864] font-semibold text-sm uppercase tracking-widest mb-3">

              <span className="w-8 h-[2px] bg-[#014864]" />

              Campus Highlights

              <span className="w-8 h-[2px] bg-[#014864]" />

            </div>

            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-800">

              Latest

              <span className="text-[#014864]">
                {" "}News & Updates
              </span>

            </h2>

            <p className="mt-4 text-slate-500 max-w-xl mx-auto">

              Explore the latest news and activities from
              across the UCTC campus.

            </p>

          </motion.div>

          {/* Search Panel */}

          <motion.div
            initial={{
              opacity: 0,
              y: 25,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.6,
            }}
            className="bg-white rounded-3xl shadow-lg shadow-slate-200/60 border border-slate-100 p-5 md:p-7 mb-10"
          >

            <div className="flex flex-col sm:flex-row gap-4">

              <div className="relative flex-1">

                <Search
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-[#014864]"
                />

                <input
                  type="text"
                  placeholder="Search news by title or date..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-12 pr-12 py-4 rounded-2xl bg-slate-50 border border-slate-200 text-slate-700 outline-none transition-all focus:bg-white focus:border-[#014864] focus:ring-4 focus:ring-[#014864]/10"
                />

                {search && (

                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-500"
                  >

                    <X size={20} />

                  </button>

                )}

              </div>

              <div className="flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-cyan-50 text-[#014864] font-semibold text-sm">

                <Newspaper size={18} />

                {filteredNews.length} News Found

              </div>

            </div>

            <div className="flex items-center justify-between mt-5 text-sm text-slate-500">

              <div className="flex items-center gap-2">

                <Clock size={15} />

                <span>
                  Latest university stories
                </span>

              </div>

              {search && (

                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="text-[#014864] font-semibold hover:underline"
                >

                  Clear Search

                </button>

              )}

            </div>

          </motion.div>

          {/* ============================================
              LOADING SKELETON
          ============================================ */}

          {loading && (

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">

              {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (

                <div
                  key={item}
                  className="animate-pulse bg-white rounded-3xl overflow-hidden border border-slate-100"
                >

                  <div className="h-52 bg-slate-200" />

                  <div className="p-5 space-y-4">

                    <div className="h-3 w-1/3 bg-slate-200 rounded-full" />

                    <div className="h-4 w-full bg-slate-200 rounded-full" />

                    <div className="h-4 w-4/5 bg-slate-200 rounded-full" />

                    <div className="h-10 w-2/3 bg-slate-200 rounded-xl" />

                  </div>

                </div>

              ))}

            </div>

          )}

          {/* ============================================
              ERROR STATE
          ============================================ */}

          {!loading && error && (

            <motion.div
              initial={{
                opacity: 0,
                scale: 0.95,
              }}
              animate={{
                opacity: 1,
                scale: 1,
              }}
              className="bg-red-50 border border-red-100 rounded-3xl p-10 text-center"
            >

              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-5">

                <RefreshCw
                  size={28}
                  className="text-red-500"
                />

              </div>

              <h3 className="text-xl font-bold text-slate-800">

                Unable to Load News

              </h3>

              <p className="mt-3 text-slate-500">

                {error}

              </p>

              <button
                onClick={newsPost}
                className="mt-6 inline-flex items-center gap-2 bg-[#014864] text-white px-6 py-3 rounded-xl hover:bg-[#036987] transition"
              >

                <RefreshCw size={18} />

                Try Again

              </button>

            </motion.div>

          )}

          {/* ============================================
              NEWS GRID
          ============================================ */}

          {!loading && !error && filteredNews.length > 0 && (

            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >

              <AnimatePresence mode="popLayout">

                {filteredNews.map((item, index) => (

                  <motion.article
                    key={item.id ?? index}
                    variants={itemVariants}
                    layout
                    initial={{
                      opacity: 0,
                      y: 30,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                    exit={{
                      opacity: 0,
                      scale: 0.95,
                    }}
                    transition={{
                      duration: 0.4,
                    }}
                    whileHover={{
                      y: -7,
                    }}
                    className="group relative bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-[#014864]/10 hover:border-[#014864]/20 transition-all duration-300"
                  >

                    {/* Image */}

                    <Link
                      to={`/news/${item.id}`}
                      className="block relative overflow-hidden"
                    >

                      {item.images_path ? (

                        <img
                          src={getImageUrl(item.images_path)}
                          alt={item.head_line || "UCTC News"}
                          loading="lazy"
                          className="w-full h-52 object-cover transition-transform duration-700 group-hover:scale-110"
                        />

                      ) : (

                        <div className="w-full h-52 bg-gradient-to-br from-[#014864] to-[#0c9db0] flex items-center justify-center">

                          <Newspaper
                            size={55}
                            className="text-white/70"
                          />

                        </div>

                      )}

                      {/* Image Overlay */}

                      <div className="absolute inset-0 bg-gradient-to-t from-[#014864]/70 via-transparent to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-500" />

                      {/* News Badge */}

                      <div className="absolute top-4 left-4">

                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-md text-[#014864] text-xs font-bold shadow-sm">

                          <Newspaper size={13} />

                          Campus News

                        </span>

                      </div>

                      {/* Floating Arrow */}

                      <motion.div
                        initial={{
                          opacity: 0,
                          scale: 0.8,
                        }}
                        whileHover={{
                          opacity: 1,
                          scale: 1,
                        }}
                        className="absolute bottom-4 right-4 w-11 h-11 rounded-full bg-white text-[#014864] flex items-center justify-center shadow-lg"
                      >

                        <ArrowUpRight size={22} />

                      </motion.div>

                    </Link>

                    {/* Content */}

                    <div className="p-5">

                      {/* Date */}

                      <div className="flex items-center gap-2 text-slate-400 text-xs font-medium mb-4">

                        <CalendarDays
                          size={15}
                          className="text-[#014864]"
                        />

                        {formatDate(item.date)}

                      </div>

                      {/* Headline */}

                      <h3 className="text-lg font-bold text-slate-800 leading-relaxed line-clamp-3 min-h-[5rem] group-hover:text-[#014864] transition-colors">

                        {item.head_line || "Untitled News"}

                      </h3>

                      {/* Bottom */}

                      <div className="mt-5 pt-4 border-t border-slate-100">

                        <Link
                          to={`/news/${item.id}`}
                          className="group/link inline-flex items-center gap-2 text-sm font-bold text-[#014864] hover:text-cyan-600 transition-colors"
                        >

                          Read Full Story

                          <ChevronRight
                            size={18}
                            className="transition-transform group-hover/link:translate-x-1"
                          />

                        </Link>

                      </div>

                    </div>

                    {/* Decorative Bottom Accent */}

                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#014864] to-cyan-400 scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-500" />

                  </motion.article>

                ))}

              </AnimatePresence>

            </motion.div>

          )}

          {/* ============================================
              EMPTY STATE
          ============================================ */}

          {!loading && !error && filteredNews.length === 0 && (

            <motion.div
              initial={{
                opacity: 0,
                y: 25,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              className="bg-white border border-slate-100 rounded-3xl p-12 md:p-16 text-center shadow-sm"
            >

              <motion.div
                animate={{
                  y: [0, -8, 0],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                }}
                className="w-24 h-24 rounded-3xl bg-cyan-50 flex items-center justify-center mx-auto mb-6"
              >

                <Newspaper
                  size={42}
                  className="text-[#014864]"
                />

              </motion.div>

              <h3 className="text-2xl font-bold text-slate-800">

                No News Found

              </h3>

              <p className="mt-3 text-slate-500 max-w-md mx-auto">

                We couldn't find any news matching your search.
                Try another keyword or clear the search.

              </p>

              {search && (

                <button
                  onClick={() => setSearch("")}
                  className="mt-7 px-6 py-3 rounded-xl bg-[#014864] text-white font-semibold hover:bg-[#036987] transition"
                >

                  Clear Search

                </button>

              )}

            </motion.div>

          )}

          {/* ============================================
              FOOTER
          ============================================ */}

          {!loading && !error && news.length > 0 && (

            <motion.div
              initial={{
                opacity: 0,
              }}
              whileInView={{
                opacity: 1,
              }}
              viewport={{
                once: true,
              }}
              className="text-center mt-12"
            >

              <div className="inline-flex items-center gap-3 bg-white border border-slate-100 shadow-sm rounded-full px-6 py-3">

                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />

                <span className="text-sm text-slate-500">

                  Showing {filteredNews.length} of {news.length} news articles

                </span>

              </div>

            </motion.div>

          )}

        </div>

      </section>

    </div>
  );
}

NewsList.propTypes = {
  logoSrc: PropTypes.string,
  accent: PropTypes.string,
  pageTitle: PropTypes.string,
};
